import { useAuth0 } from '@auth0/auth0-react';
import styled from '@emotion/styled';
import {
  Group as AudiencesIcon,
  colors,
  DashboardIcon,
  ErrorWarning,
  Finance,
  ManageIcon,
  NavBarButton,
  Finance as Performance,
  ProximaLogoNova,
  NavBar as ProximaNavBar
} from '@innovationdepartment/proxima-ui';
import type { IUser } from '@innovationdepartment/proxima-ui/dist/esm/types/user';
import {
  AccountTreeOutlined,
  ArticleOutlined,
  CategoryOutlined,
  Insights,
  PersonSearchOutlined,
  TuneOutlined
} from '@mui/icons-material';
import { useBackgroundConditions } from 'hooks';
import { Link, useNavigate } from 'react-router';
import { useBrandStore } from 'stores';

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const NAVBAR_COLORS = {
  logo: colors.legacy.offWhite,
  bg: colors.legacy.tealDarkest,
  bgHover: colors.legacy.tealDarker,
  color: colors.legacy.mistDarker,
  colorHover: colors.legacy.offWhite
};

const NavBar = () => {
  const { user, logout } = useAuth0();
  const { brand, clearBrandStore } = useBrandStore();
  const { showNavbar } = useBackgroundConditions();
  const navigate = useNavigate();

  if (!showNavbar) return null;

  const isButtonActive = (buttonPathname: string) => window.location.pathname === buttonPathname;

  const onLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
    clearBrandStore();
  };

  if (!user) return null;

  const userDetails: IUser = {
    firstName: user.given_name as string,
    lastName: user.family_name as string,
    email: user.email as string
  };

  const navbarButtons = [
    {
      label: 'Overview',
      pathname: `/brand/${brand.brandId}`,
      icon: <DashboardIcon />
    },
    {
      button: { icon: <Insights />, label: 'Insights' },
      isActive: false,
      drawerItems: {
        items: [
          {
            items: [
              {
                key: 'trends',
                label: 'Trends',
                pathname: `/brand/${brand.brandId}/insights/trends`
              },
              {
                key: 'benchmarks',
                label: 'Benchmarks',
                pathname: `/brand/${brand.brandId}/insights/chart`
              }
            ],
            label: 'PERFORMANCE',
            icon: <Performance />
          }
        ],
        onClick: (pathname: string) => navigate(pathname),
        title: 'Insights'
      }
    },
    {
      button: { icon: <PersonSearchOutlined />, label: 'Customer Explorer' },
      isActive: false,
      drawerItems: {
        items: [
          {
            // NavbarButtonDrawer keys each section by label; omit it and React warns about missing keys
            label: '',
            items: [
              {
                key: 'customer-overview',
                label: 'Customer Overview',
                pathname: `/brand/${brand.brandId}/customer-overview`
              },
              {
                key: 'top-personas',
                label: 'Top Personas',
                pathname: `/brand/${brand.brandId}/personas`
              },
              {
                key: 'persona-form',
                label: 'Persona Form',
                pathname: `/brand/${brand.brandId}/personas-form`
              }
            ]
          }
        ],
        onClick: (pathname: string) => navigate(pathname),
        title: 'Customer Explorer'
      }
    },
    {
      label: 'Audiences',
      pathname: `/brand/${brand.brandId}/audiences`,
      icon: <AudiencesIcon />
    },
    {
      label: 'Custom Audiences',
      pathname: `/brand/${brand.brandId}/custom-audiences`,
      icon: <TuneOutlined />
    },
    {
      label: 'Manage',
      pathname: `/brand/${brand.brandId}/manage`,
      icon: <ManageIcon />
    },
    {
      label: 'Agent AI',
      pathname: `/brand/${brand.brandId}/agent`,
      icon: <Finance />
    },
    {
      label: 'Products',
      pathname: `/brand/${brand.brandId}/products`,
      icon: <CategoryOutlined />
    },
    {
      label: 'Creative Engine',
      pathname: `/brand/${brand.brandId}/creative-engine`,
      icon: <ArticleOutlined />
    },
    {
      label: 'Workflows',
      pathname: `/brand/${brand.brandId}/workflows`,
      icon: <AccountTreeOutlined />
    }
  ];

  const config = {
    logo: (
      <Link to="/brands">
        <ProximaLogoNova />
      </Link>
    ),
    accountMenuItems: [
      {
        label: 'Logout',
        pathname: '',
        onClick: onLogout
      }
    ],
    brandColors: NAVBAR_COLORS
  };

  const navbarBottomButtons = [
    {
      key: 'warnings',
      component: (
        <StyledLink to={`/brand/${brand.brandId}/warnings`}>
          <ErrorWarning />
        </StyledLink>
      )
    }
  ];

  return (
    <ProximaNavBar
      logo={config.logo}
      accountMenuItems={config.accountMenuItems}
      pathname={window.location.pathname}
      user={userDetails}
      navbarMenuItems={navbarBottomButtons}
    >
      {navbarButtons.map(button => {
        const hasDrawer = button.button;

        if (hasDrawer)
          return (
            <NavBarButton
              button={button.button}
              isActive={button.isActive}
              key={button.button.label}
              drawerItems={button.drawerItems}
            />
          );

        const isActive = isButtonActive(button.pathname);

        return (
          <StyledLink to={button.pathname} key={button.label}>
            <NavBarButton button={button} isActive={isActive} key={button.label} />
          </StyledLink>
        );
      })}
    </ProximaNavBar>
  );
};

export default NavBar;
